import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaBox, FaShoppingCart, FaStar } from "react-icons/fa";
import { toast } from "react-toastify";
import b2bHubHomeService from "../../services/b2bHubHome.service";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import { addMarketplaceCartItem } from "../../services/marketplaceCart.service";
import { formatCartInr } from "../../utils/marketplaceCart.util";
import "../../assets/styles/marketplace_related_products.scss";

function StarRating({ value = 4.8 }) {
  return (
    <div className="marketplace-reco__card-rating" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <FaStar key={star} color={star <= Math.round(value) ? "#f97316" : "#e2e8f0"} />
      ))}
      <span>{value}</span>
    </div>
  );
}

export function normalizeRecoProduct(product) {
  const id = Number(product.id || product.product_id);
  const title = product.title || product.product_name || product.name || "Product";
  const imageUrl =
    product.image_path ||
    product.imageUrl ||
    (Array.isArray(product.carousel_images) ? product.carousel_images[0] : null);
  const unit = product.moq_unit || product.moq_unit_of_measurement || product.unit || "Unit";
  const moq = Math.max(1, Number(product.moq) || 1);
  const priceLabel =
    product.price_label ||
    (product.price_amount != null && Number(product.price_amount) > 0
      ? `${formatCartInr(product.price_amount)}${unit ? ` / ${unit}` : ""}`
      : null) ||
    (() => {
      const amount = Number(product.price_with_tax ?? product.price_without_tax ?? product.price ?? 0);
      return amount > 0 ? `${formatCartInr(amount)}${unit ? ` / ${unit}` : ""}` : "—";
    })();
  const unitLabel =
    product.moq_label ||
    (product.moq != null && String(product.moq).trim() !== ""
      ? `MOQ: ${product.moq} ${unit}`
      : unit);

  return { id, title, imageUrl, priceLabel, unitLabel, moq };
}

export default function MarketplaceRelatedProductsSection({
  excludeProductIds = [],
  saving = false,
  title = "You may also be interested in",
}) {
  const [recommendations, setRecommendations] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await b2bHubHomeService.getMarketplace({ page: 1, limit: 12 });
        const products = res?.data?.products || res?.data?.data?.products || res?.products || [];
        if (!cancelled) setRecommendations(Array.isArray(products) ? products : []);
      } catch {
        if (!cancelled) setRecommendations([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleProducts = useMemo(() => {
    const excludeSet = new Set(excludeProductIds.map((id) => Number(id)).filter(Boolean));
    return recommendations
      .map(normalizeRecoProduct)
      .filter((product) => product.id && !excludeSet.has(product.id))
      .slice(0, 4);
  }, [recommendations, excludeProductIds]);

  const handleAddToCart = async (product) => {
    if (!product?.id || saving || adding) return;
    setAdding(true);
    try {
      await addMarketplaceCartItem(product.id, product.moq);
      toast.success("Added to cart.");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not add to cart.");
    } finally {
      setAdding(false);
    }
  };

  if (!visibleProducts.length) return null;

  const isBusy = saving || adding;

  return (
    <section className="marketplace-reco" aria-label="Recommended products">
      <div className="marketplace-reco__head">
        <h2>{title}</h2>
        <Link to="/marketplace/products">
          View All <FaArrowRight aria-hidden />
        </Link>
      </div>
      <div className="marketplace-reco__grid">
        {visibleProducts.map((product) => (
          <article key={product.id} className="marketplace-reco__card">
            <Link
              to={`/marketplace/products/${product.id}`}
              className="marketplace-reco__card-media"
              aria-label={`View ${product.title}`}
            >
              <div className="marketplace-reco__card-thumb">
                {product.imageUrl ? (
                  <img src={resolvePublicFileUrl(product.imageUrl)} alt={product.title} />
                ) : (
                  <FaBox />
                )}
              </div>
            </Link>
            <h3>
              <Link to={`/marketplace/products/${product.id}`}>{product.title}</Link>
            </h3>
            <p className="marketplace-reco__card-unit">{product.unitLabel}</p>
            <StarRating value={4.8} />
            <p className="marketplace-reco__card-price">{product.priceLabel}</p>
            <button
              type="button"
              className="marketplace-reco__card-add"
              disabled={isBusy}
              onClick={() => handleAddToCart(product)}
            >
              <FaShoppingCart aria-hidden /> Add to Cart
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}
